/** Aligns with `MyBreaksPage` progression; used for Club Stats visibility. */
import { hasSuperUserAccess } from './sessionUser';

const GRADE_ORDER = {
  Beginner: 0,
  'Yellow Belt': 1,
  'Green Belt': 2,
  'Red Belt': 3,
  'Brown Belt': 4,
  'Shodan Ho': 5,
  '1st Degree Black Belt': 6,
  '2nd Degree Black Belt': 7,
  '3rd Degree Black Belt': 8,
  '4th Degree Black Belt': 9,
  '5th Degree Black Belt': 10,
  '6th Degree Black Belt': 11,
  '7th Degree Black Belt': 12,
  '8th Degree Black Belt': 13,
  '9th Degree Black Belt': 14,
  '10th Degree Black Belt': 15,
};

const NON_BLACK_BELT_GRADES = new Set([
  'Beginner',
  'Yellow Belt',
  'Green Belt',
  'Red Belt',
  'Brown Belt',
  'Shodan Ho',
]);

/** DB `grades.grade_rank_order` for 1st Degree Black Belt */
const FIRST_DEGREE_DB_RANK = 7;

/** @param {import('./sessionUser').StudentUser|null|undefined} user */
export function viewerGradeRank(user) {
  if (!user) return null;
  if (user.student_grade_rank != null && !Number.isNaN(Number(user.student_grade_rank))) {
    return Number(user.student_grade_rank);
  }
  if (user.student_grade && GRADE_ORDER[user.student_grade] != null) {
    return GRADE_ORDER[user.student_grade];
  }
  return null;
}

/**
 * True when grade is more than one rank above the viewer (not selectable).
 * @param {{ grade_rank_order?: number|null }} grade
 * @param {import('./sessionUser').StudentUser|null|undefined} user
 */
export function isGradeBeyondViewerAccess(grade, user) {
  if (!grade || !user) return false;
  if (hasSuperUserAccess(user)) return false;
  const viewerRank = viewerGradeRank(user);
  const gradeRank =
    grade.grade_rank_order != null ? Number(grade.grade_rank_order) : null;
  if (viewerRank == null || gradeRank == null) return false;
  return gradeRank > viewerRank + 1;
}

/** Default grade picker value: one rank above the viewer, else current grade. */
export function findDefaultNextGrade(grades, user) {
  if (!Array.isArray(grades) || grades.length === 0) return null;
  const viewerRank = viewerGradeRank(user);
  if (viewerRank == null) return grades[0];
  const next = grades.find((g) => Number(g.grade_rank_order) === viewerRank + 1);
  if (next) return next;
  const current = grades.find((g) => Number(g.grade_rank_order) === viewerRank);
  return current ?? grades[0];
}

export function isFirstDegreeOrHigher(user) {
  if (!user) return false;

  const gradeName = String(user.student_grade || '').trim();
  if (gradeName) {
    if (NON_BLACK_BELT_GRADES.has(gradeName)) return false;
    const r = GRADE_ORDER[gradeName];
    const min = GRADE_ORDER['1st Degree Black Belt'];
    if (r != null && min != null) return r >= min;
  }

  const rank =
    user.student_grade_rank != null && !Number.isNaN(Number(user.student_grade_rank))
      ? Number(user.student_grade_rank)
      : null;
  // Only trust rank alone when grade name is missing (avoid stale rank vs current belt name).
  if (rank != null && !gradeName) return rank >= FIRST_DEGREE_DB_RANK;

  return false;
}

/** DB `grades.grade_rank_order` for 2nd Degree Black Belt (1st is 7). */
const SECOND_DEGREE_DB_RANK = 8;

export function isSecondDegreeOrHigher(user) {
  if (!user) return false;
  if (hasSuperUserAccess(user)) return true;

  const gradeName = String(user.student_grade || '').trim();
  if (gradeName) {
    if (NON_BLACK_BELT_GRADES.has(gradeName)) return false;
    if (gradeName === '1st Degree Black Belt') return false;
    const r = GRADE_ORDER[gradeName];
    const min = GRADE_ORDER['2nd Degree Black Belt'];
    if (r != null && min != null) return r >= min;
  }

  const rank =
    user.student_grade_rank != null && !Number.isNaN(Number(user.student_grade_rank))
      ? Number(user.student_grade_rank)
      : null;
  if (rank != null && !gradeName) return rank >= SECOND_DEGREE_DB_RANK;

  return false;
}
